import { formatCurrency, formatDate, titleCase } from '@/utils';
import type { Policy } from '@/types';

/**
 * Field builders for the Policy Detail page's "Premium Details" and "Vehicle
 * Details" cards.
 *
 * Both cards used to render a hand-picked subset of what the broker actually
 * entered — three premium figures out of fifteen, nine vehicle fields out of
 * twenty-two — so values captured at issuance were invisible for the rest of
 * the policy's life. These builders render everything the detail serializer
 * returns, and nothing that has no value.
 *
 * Kept out of the page component so the "what shows and what is hidden" rules
 * are unit-testable without mounting the whole detail screen.
 */

/** Premium columns `PolicyDetailSerializer` returns that the shared `Policy`
 *  type does not declare yet. `src/types/index.ts` is outside this task's file
 *  domain, so they live here and are applied with a single cast on the page —
 *  fold them into `Policy` the next time that file is edited. */
export interface PolicyPremiumFields {
  gst_amount?: string | null;
  stamp_duty?: string | null;
  od_premium?: string | null;
  od_tax?: string | null;
  tp_premium?: string | null;
  tp_tax?: string | null;
  pa_premium?: string | null;
  addon_premium?: string | null;
  ncb_discount?: string | null;
  ncb_percentage?: number | null;
  other_discounts?: string | null;
  sum_insured?: string | null;
  idv?: string | null;
}

/** One rider from `PolicyAddOn`. `Policy.addon_premium` is only the roll-up,
 *  so a policy sold with three riders showed one opaque figure that could not
 *  be reconciled against what was quoted. */
export interface PolicyAddOnLine {
  id?: string;
  addon_name?: string | null;
  addon_code?: string | null;
  sum_insured?: string | null;
  premium?: string | null;
  gst_amount?: string | null;
}

/** One stored `PolicyPremiumBreakdown` row — the insurer's own itemisation.
 *  `is_discount` matters: without it a subtraction is indistinguishable from
 *  a charge. */
export interface PolicyPremiumComponent {
  id?: string;
  component_name?: string | null;
  component_type?: string | null;
  component_code?: string | null;
  amount?: string | null;
  percentage?: string | null;
  base_amount?: string | null;
  is_tax?: boolean;
  is_discount?: boolean;
  is_visible?: boolean;
  sequence_order?: number | null;
}

/** The full nested `PolicyVehicle` row (`PolicyVehicleDetailSerializer`).
 *  Every field is optional here: only quote-originated motor policies carry a
 *  PolicyVehicle row at all, and several of its columns are nullable. */
export interface PolicyVehicleDetail {
  id?: string;
  make_name?: string | null;
  model_name?: string | null;
  variant_name?: string | null;
  registration_number?: string | null;
  registration_date?: string | null;
  rto?: string | null;
  rto_name?: string | null;
  rto_code?: string | null;
  engine_number?: string | null;
  chassis_number?: string | null;
  manufacturing_year?: number | null;
  fuel_type?: string | null;
  color?: string | null;
  engine_cc?: number | null;
  seating_capacity?: number | null;
  gross_vehicle_weight?: number | null;
  idv?: string | null;
  is_financed?: boolean;
  financier_name?: string | null;
  financier_address?: string | null;
  electrical_accessories?: string | null;
  non_electrical_accessories?: string | null;
  cng_lpg_kit?: string | null;
  customer_vehicle?: string | null;
}

/** A policy as the DETAIL endpoint actually returns it. */
export type PolicyWithBreakdown = Omit<Policy, 'vehicle_details'> &
  PolicyPremiumFields & {
    /** Flat columns on Policy itself, set by broker-entered motor policies
     *  that have no PolicyVehicle row. */
    registration_date?: string | null;
    manufacturing_year?: number | null;
    vehicle_details?: PolicyVehicleDetail | null;
    addons?: PolicyAddOnLine[] | null;
    premium_breakdown?: PolicyPremiumComponent[] | null;
  };

export interface DetailField {
  label: string;
  value: string;
}

export interface DetailGroup {
  title: string;
  fields: DetailField[];
}

/**
 * Format a money column, or return null when there is nothing to show.
 *
 * Every premium component and accessory column on the model is
 * `DecimalField(default=0)`, so a component that does not apply to this policy
 * arrives as "0.00", not null — a health policy carries `od_premium="0.00"`.
 * Rendering that as "₹0" reads as "we charged zero for own damage" rather than
 * "own damage is not part of this policy", so zero counts as no value.
 */
export function premiumAmount(value?: string | number | null): string | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return null;
  return formatCurrency(numeric);
}

/**
 * Same formatting, but zero is a value.
 *
 * The three headline figures (net / total / gross) are entered on every single
 * policy, so a 0 there says "this policy's net premium is zero" — not "this
 * component does not apply", which is what a 0 means for the OD/TP/PA/addon
 * columns. Applying the hide-when-zero rule to them would turn a real,
 * deliberately-entered figure into a missing row.
 */
export function premiumAmountAlways(value?: string | number | null): string | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;
  return formatCurrency(numeric);
}

/** Same rule for whole-number columns (engine cc, seating capacity, ...). */
function count(value: number | null | undefined, suffix = ''): string | null {
  if (value === null || value === undefined) return null;
  if (!Number.isFinite(value) || value === 0) return null;
  return suffix ? `${value} ${suffix}` : String(value);
}

function text(value?: string | null): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/** A string key of the policy's `policy_data` bag, or null. */
function policyDataText(policy: PolicyWithBreakdown, key: string): string | null {
  // `policy_data` is on the detail payload but not on the shared Policy type.
  const bag = (policy as unknown as { policy_data?: unknown }).policy_data;
  if (!bag || typeof bag !== 'object') return null;
  const value = (bag as Record<string, unknown>)[key];
  return typeof value === 'string' ? text(value) : null;
}

/** Drop every pair whose value came back null, keeping the declared order. */
function fields(pairs: [string, string | null][]): DetailField[] {
  return pairs
    .filter((pair): pair is [string, string] => pair[1] !== null)
    .map(([label, value]) => ({ label, value }));
}

function nonEmpty(groups: DetailGroup[]): DetailGroup[] {
  return groups.filter((group) => group.fields.length > 0);
}

/**
 * The whole Premium Details card: the three headline figures followed by every
 * component the broker entered, grouped the way a motor policy schedule reads.
 *
 * Net/total/gross used to be rendered separately by the page as three centred,
 * boxed tiles above these rows, so one card spoke two visual languages. They
 * are built here now so the card is a single uniform list (A5).
 */
export function buildPremiumGroups(policy: PolicyWithBreakdown): DetailGroup[] {
  const ncbPercentage =
    policy.ncb_percentage && policy.ncb_percentage > 0 ? `${policy.ncb_percentage}%` : null;

  // The riders behind the `addon_premium` roll-up. A rider that costs nothing
  // is still part of the cover, so it reads "Included" rather than vanishing.
  const addonLines: DetailField[] = (policy.addons ?? []).map((addon, index) => ({
    label: text(addon.addon_name) ?? text(addon.addon_code) ?? `Add-on ${index + 1}`,
    value: premiumAmount(addon.premium) ?? 'Included',
  }));

  // The insurer's own itemisation, when the policy came from a quote.
  const componentLines: DetailField[] = [...(policy.premium_breakdown ?? [])]
    .filter((component) => component.is_visible !== false)
    .sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0))
    .map((component) => {
      const amount = premiumAmount(component.amount);
      if (amount === null) return null;
      const percentage = Number(component.percentage);
      const label =
        text(component.component_name) ??
        text(component.component_code) ??
        text(component.component_type) ??
        'Component';
      return {
        label: Number.isFinite(percentage) && percentage !== 0 ? `${label} (${percentage}%)` : label,
        value: component.is_discount ? `- ${amount}` : amount,
      };
    })
    .filter((line): line is DetailField => line !== null);

  return nonEmpty([
    {
      title: 'Premium',
      // premiumAmountAlways, not premiumAmount: these two keep a zero.
      //
      // B5: Gross Premium used to sit here as a third row and was dropped on
      // request -- on a hand-entered policy it is always identical to Total,
      // because that form derives total = net + tax and has no stamp-duty box,
      // so it read as the same number twice.
      //
      // THE COLUMN IS NOT GONE, and it is not always equal to total. A quote
      // computes gross = net + gst and then total = gross + stamp_duty, so the
      // two diverge wherever stamp duty applies. `Policy.gross_premium` stays
      // NOT NULL and stays written: commission can be configured to use it as
      // its basis (PREMIUM_BASE_CHOICES), renewals read it for the high-value
      // gate, and the pro-rata cancellation refund is computed from it. Adding
      // it back here is a one-line change; removing any WRITER of it is not.
      fields: fields([
        ['Net Premium', premiumAmountAlways(policy.net_premium)],
        ['Total Premium', premiumAmountAlways(policy.total_premium)],
      ]),
    },
    {
      title: 'Own Damage',
      fields: fields([
        ['OD Premium', premiumAmount(policy.od_premium)],
        ['OD Tax', premiumAmount(policy.od_tax)],
      ]),
    },
    {
      title: 'Third Party',
      fields: fields([
        ['TP Premium', premiumAmount(policy.tp_premium)],
        ['TP Tax', premiumAmount(policy.tp_tax)],
      ]),
    },
    {
      title: 'Other Components',
      fields: fields([
        ['PA Premium', premiumAmount(policy.pa_premium)],
        ['Add-on Premium', premiumAmount(policy.addon_premium)],
      ]),
    },
    { title: 'Add-ons', fields: addonLines },
    {
      title: 'Taxes & Charges',
      fields: fields([
        ['GST', premiumAmount(policy.gst_amount)],
        ['Stamp Duty', premiumAmount(policy.stamp_duty)],
      ]),
    },
    {
      title: 'Discounts',
      fields: fields([
        ['NCB', ncbPercentage],
        ['NCB Discount', premiumAmount(policy.ncb_discount)],
        ['Other Discounts', premiumAmount(policy.other_discounts)],
      ]),
    },
    {
      title: 'Coverage',
      // "Sum Assured" is the term brokers and every non-motor policy schedule
      // use; motor's equivalent is IDV, which lives on the vehicle card. The
      // column is still `sum_insured` — this is a label, not a field rename.
      fields: fields([['Sum Assured', premiumAmount(policy.sum_insured)]]),
    },
    { title: 'Premium Components', fields: componentLines },
  ]);
}

/**
 * The vehicle breakdown. Reads the nested PolicyVehicle row when there is one
 * (quote-originated motor policies) and falls back to the flat columns on
 * Policy itself for broker-entered motor policies, which have no such row.
 *
 * Make / Model / Variant / Registration Number always render — a vehicle card
 * missing its own identity reads as broken — while everything else is hidden
 * when it was never entered.
 */
export function buildVehicleGroups(policy: PolicyWithBreakdown): DetailGroup[] {
  const vehicle = policy.vehicle_details ?? undefined;

  const rtoLabel =
    [text(vehicle?.rto_code), text(vehicle?.rto_name)].filter(Boolean).join(' — ') || null;

  const financing = vehicle?.is_financed
    ? fields([
        ['Financed', 'Yes'],
        ['Financier', text(vehicle.financier_name)],
        ['Financier Address', text(vehicle.financier_address)],
      ])
    : [];

  return nonEmpty([
    {
      title: 'Vehicle',
      fields: fields([
        ['Make', text(vehicle?.make_name) ?? text(policy.vehicle_make_name) ?? '-'],
        ['Model', text(vehicle?.model_name) ?? text(policy.vehicle_model_name) ?? '-'],
        ['Variant', text(vehicle?.variant_name) ?? text(policy.vehicle_variant_name) ?? '-'],
        // Category and segment come from the vehicle master, via the model the
        // policy points at -- and they are two of the dimensions a commission
        // rate can now be scoped on, so the page has to show what a rate will
        // be matched against. Blank for a policy with no model, and `fields()`
        // drops a blank row rather than printing "-".
        ['Category', text(policy.vehicle_category)],
        ['Segment', text(policy.vehicle_segment)],
        [
          'Manufacturing Year',
          count(vehicle?.manufacturing_year ?? policy.manufacturing_year ?? null),
        ],
        ['Colour', text(vehicle?.color)],
        [
          // B1: `Policy.fuel_type` is a FK to the Country-Admin master and is
          // the only fuel a broker-entered policy can have (those carry no
          // PolicyVehicle row at all). The legacy free-text
          // `PolicyVehicle.fuel_type` stays as the fallback so the ~200k
          // policies recorded before the FK existed still show something.
          'Fuel Type',
          text(policy.fuel_type_name) ??
            (text(vehicle?.fuel_type) ? titleCase(text(vehicle?.fuel_type) as string) : null),
        ],
      ]),
    },
    {
      title: 'Registration',
      fields: fields([
        [
          'Registration Number',
          text(vehicle?.registration_number) ?? text(policy.vehicle_number) ?? '-',
        ],
        [
          'Registration Date',
          formatDate(vehicle?.registration_date ?? policy.registration_date ?? null) || null,
        ],
        ['RTO', rtoLabel],
        // B-04: a broker-entered policy has no PolicyVehicle; the Add/Edit
        // form keeps these two in `policy_data` instead.
        ['Engine Number', text(vehicle?.engine_number) ?? policyDataText(policy, 'engine_number')],
        ['Chassis Number', text(vehicle?.chassis_number) ?? policyDataText(policy, 'chassis_number')],
      ]),
    },
    {
      title: 'Specifications',
      fields: fields([
        ['Engine Capacity', count(vehicle?.engine_cc ?? null, 'cc')],
        ['Seating Capacity', count(vehicle?.seating_capacity ?? null)],
        ['Gross Vehicle Weight', count(vehicle?.gross_vehicle_weight ?? null, 'kg')],
        ['IDV', premiumAmount(vehicle?.idv ?? policy.idv)],
      ]),
    },
    { title: 'Financing', fields: financing },
    {
      title: 'Accessories Covered',
      fields: fields([
        ['Electrical Accessories', premiumAmount(vehicle?.electrical_accessories)],
        ['Non-Electrical Accessories', premiumAmount(vehicle?.non_electrical_accessories)],
        ['CNG / LPG Kit', premiumAmount(vehicle?.cng_lpg_kit)],
      ]),
    },
  ]);
}
